import { Navigate, useLocation } from '@tanstack/react-router'

/**
 * A redirect that stops once it has been obeyed.
 *
 * `<Navigate>` is NOT idempotent across re-renders. Its guard compares the JSX
 * props OBJECT (useNavigate.js: `previousPropsRef.current !== props`), and JSX
 * allocates a fresh one every render — so a layout that re-renders navigates
 * again, and again. A repeat navigate to the URL already in `latestLocation`
 * does not no-op either; router-core calls `load()` directly, which sets state
 * from inside a layout effect. React counts fifty of those and throws #185.
 *
 * That alone is survivable when the redirect's target lives INSIDE the
 * redirecting layout's own subtree, because the commit unmounts the layout and
 * the cycle ends. It is not survivable when the target is OUTSIDE it: the new
 * matches only swap in at `onReady`, each fresh navigate cancels the pending
 * load that would have got there, and the layout is still mounted to do it all
 * again. `stores.location` is rewritten before any of that commits, so a layout
 * subscribed to `useLocation()` re-renders on every turn of the loop.
 *
 * The fixpoint is the missing piece: once the location already IS the target,
 * render nothing and let the in-flight transition finish. Anything that
 * redirects out of its own subtree needs this; prefer it to `<Navigate>` in a
 * layout, always.
 */
export function RedirectOnce({ to }: { to: string }) {
  const { pathname } = useLocation()

  // Already heading there — say nothing rather than asking a second time.
  if (pathname === to) return null

  return <Navigate to={to} replace />
}
